import * as React from 'react';
import { formatActionEvent } from './formatEvent';
import { defaultSend, defaultGetCurrentTimestamp, defaultGetPageId, defaultGetSessionId, defaultGetTrackingId, defaultGetUserAgent, } from './defaultSideEffects';
import { shallowEqualObjects, equalCapturedMetrics } from './compareObjects';
/**
 * Side Effects are ways of reading or writing data that's
 * not passed as parameters or returned as results. For this library,
 * we need to read values from cookies and the browser context, and we
 * need to send requests to the events API endpoint
 */
const defaultSideEffects = {
    sendMetric: defaultSend,
    getCurrentTimestamp: defaultGetCurrentTimestamp,
    getPageId: defaultGetPageId,
    getSessionId: defaultGetSessionId,
    getTrackingId: defaultGetTrackingId,
    getUserAgent: defaultGetUserAgent,
};
const { Provider: SideEffectsProvider, Consumer: SideEffectsConsumer, } = React.createContext(defaultSideEffects);
/**
 * The application will provide production-ready side effects as default,
 * but we still expose this Provider in order to facilitate mocks or
 * override any of the side effects
 */
export class MetricsProvider extends React.Component {
    constructor() {
        super(...arguments);
        this.cachedSideEffects = defaultSideEffects;
    }
    render() {
        const { sendMetric = defaultSend, getCurrentTimestamp = defaultGetCurrentTimestamp, getPageId = defaultGetPageId, getSessionId = defaultGetSessionId, getTrackingId = defaultGetTrackingId, getUserAgent = defaultGetUserAgent, children, } = this.props;
        const sideEffects = {
            sendMetric,
            getCurrentTimestamp,
            getPageId,
            getSessionId,
            getTrackingId,
            getUserAgent,
        };
        /**
         * If the user does not change the passed props, we should not update the
         * value in the provider. If we pass a new object to the provider - even
         * with the same values - it will retrigger render of ALL THE CONSUMERS!
         *
         * Be careful, it's going to KILL your application performance!
         *
         *  Reference: https://reactjs.org/docs/context.html#caveats
         */
        if (!shallowEqualObjects(sideEffects, this.cachedSideEffects)) {
            this.cachedSideEffects = sideEffects;
        }
        return (React.createElement(SideEffectsProvider, { value: this.cachedSideEffects }, children));
    }
}
const initialCapturedMetrics = {};
const { Provider: CapturedMetricsProvider, Consumer: CapturedMetricsConsumer, } = React.createContext(initialCapturedMetrics);
/**
 * This component should be mounted in the React tree, describing the
 * different sections, components, etc. The data contained in the
 * props passed to the components will populate the metrics sent.
 *
 * Have a look at the MetricsCapturingSchema type definition for all
 * the expected props to be passed to this component
 */
export class Metrics extends React.Component {
    constructor() {
        super(...arguments);
        this.cachedMetrics = initialCapturedMetrics;
    }
    render() {
        return (React.createElement(CapturedMetricsConsumer, null, metrics => {
            const propsWithoutChildren = Object.assign({}, this.props);
            propsWithoutChildren.children = undefined;
            const nextMetrics = Object.assign({}, metrics, propsWithoutChildren);
            /**
             * We test for this equality for the same reason we do it
             * in the MetricsProvider component
             */
            if (!equalCapturedMetrics(nextMetrics, this.cachedMetrics)) {
                this.cachedMetrics = nextMetrics;
            }
            return (React.createElement(CapturedMetricsProvider, { value: this.cachedMetrics }, this.props.children));
        }));
    }
}
/**
 * Every element among MetricsClickable children will send a metric on click.
 *
 * It will send all the metric data passed to the Metrics components located
 * before the MetricsClickable, and also the data passed as props
 * to MetricsClickable itself.
 */
export class MetricsClickable extends React.Component {
    constructor() {
        super(...arguments);
        this.sendMetric = (sideEffects, allMetricData) => (ev) => {
            const sideEffectsData = {
                browserTimestamp: sideEffects.getCurrentTimestamp(),
                pageId: sideEffects.getPageId(),
                sessionId: sideEffects.getSessionId(),
                trackingId: sideEffects.getTrackingId(),
                userAgent: sideEffects.getUserAgent(),
            };
            const metric = formatActionEvent(sideEffectsData, allMetricData);
            return sideEffects.sendMetric(metric);
        };
    }
    render() {
        const { children, className = '' } = this.props;
        return (React.createElement(CapturedMetricsConsumer, null, metrics => {
            // interactionType and initiator come first because they
            // can be overridden by the user
            const allMetricData = Object.assign({ interactionType: 'click', initiator: 'user' }, metrics, this.props, { children: undefined });
            return (React.createElement(SideEffectsConsumer, null, sideEffects => (React.createElement("span", { className: className, onClickCapture: this.sendMetric(sideEffects, allMetricData) }, children))));
        }));
    }
}
/**
 * CustomMetrics expose a `sendMetric` function to be called manually,
 * using the Render Props pattern ( https://reactjs.org/docs/render-props.html )
 */
export class CustomMetrics extends React.Component {
    constructor() {
        super(...arguments);
        this.sendMetric = (sideEffects, capturedMetrics) => (additionalMetricsData = {}) => {
            const sideEffectsData = {
                browserTimestamp: sideEffects.getCurrentTimestamp(),
                pageId: sideEffects.getPageId(),
                sessionId: sideEffects.getSessionId(),
                trackingId: sideEffects.getTrackingId(),
                userAgent: sideEffects.getUserAgent(),
            };
            const metricsData = Object.assign({}, capturedMetrics, additionalMetricsData);
            const metric = formatActionEvent(sideEffectsData, metricsData);
            return sideEffects.sendMetric(metric);
        };
        this.cachedSideEffects = defaultSideEffects;
        this.cachedMetrics = initialCapturedMetrics;
        this.cachedSendMetric = this.sendMetric(this.cachedSideEffects, this.cachedMetrics);
    }
    render() {
        return (React.createElement(CapturedMetricsConsumer, null, metrics => (React.createElement(SideEffectsConsumer, null, sideEffects => {
            /**
             * We'll cache the sendMetric function too, to
             * make sure we do not cause unnecessary rerenders.
             *
             * We only need to do !== comparison, because the
             * Providers are already doing the deep comparisons on
             * the metrics and sideEffects values.
             */
            if (metrics !== this.cachedMetrics ||
                sideEffects !== this.cachedSideEffects) {
                this.cachedSideEffects = sideEffects;
                this.cachedMetrics = metrics;
                this.cachedSendMetric = this.sendMetric(this.cachedSideEffects, this.cachedMetrics);
            }
            return this.props.children({
                sendMetric: this.cachedSendMetric,
            });
        }))));
    }
}
export function withCustomMetrics(options) {
    return function (InputComponent) {
        return function (props) {
            return (React.createElement(Metrics, Object.assign({}, options),
                React.createElement(CustomMetrics, null, ({ sendMetric }) => (React.createElement(InputComponent, Object.assign({ sendMetric: sendMetric }, props))))));
        };
    };
}
